import { Popover, PopoverContent, PopoverTrigger } from '@/components/_shadcn-ui/popover';
import { cn } from '@/utils/cn';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../checkbox';

interface RenderOptionProps<T> {
  option: T;
  isSelected: boolean;
  label: string;
  onToggle: (checked: boolean) => void;
}

interface MultiSelectDropdownProps<T extends Record<string, unknown>> {
  // UI configs
  placeholder?: string;
  searchPlaceholder?: string;
  noOptionsFoundText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isWithSearch?: boolean;
  isDisplaySelectedItem?: boolean;
  limit?: number;

  // Data
  options: T[];
  value: T[];
  labelKey: keyof T;
  valueKey: keyof T;

  // Custom renderers
  renderOption?: (props: RenderOptionProps<T>) => React.ReactNode;
  renderSelectedItem?: (option: T, onRemove: () => void) => React.ReactNode;
  onChange: (selected: T[]) => void;
}

const MultiSelectDropdown = <T extends Record<string, unknown>>({
  options,
  value,
  onChange,
  labelKey,
  valueKey,
  placeholder = 'Select options',
  searchPlaceholder = 'Search...',
  noOptionsFoundText = 'No options found!',
  disabled = false,
  isLoading = false,
  isWithSearch = true,
  isDisplaySelectedItem = false,
  limit = 2,
  renderOption,
  renderSelectedItem,
}: MultiSelectDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  const getLabel = useCallback((option: T) => String(option?.[labelKey] ?? ''), [labelKey]);
  const getValue = useCallback((option: T) => String(option?.[valueKey] ?? ''), [valueKey]);

  const filteredOptions = useMemo(
    () => options.filter((opt) => getLabel(opt).toLowerCase().includes(search.toLowerCase())),
    [options, search, getLabel]
  );

  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 6,
  });

  // Force virtualizer to recalculate when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        rowVirtualizer.measure();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, rowVirtualizer]);

  // Use Set for O(1) lookup instead of O(n) array.some()
  const selectedIds = useMemo(() => new Set(value.map(getValue)), [value, getValue]);

  const isSelected = useCallback((option: T) => selectedIds.has(getValue(option)), [selectedIds, getValue]);

  const handleToggle = useCallback(
    (option: T, checked: boolean) => {
      const newValue = checked ? [...value, option] : value.filter((item) => getValue(item) !== getValue(option));
      onChange(newValue);
    },
    [value, onChange, getValue]
  );

  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange([]);
    },
    [onChange]
  );

  const handleSelectAll = useCallback(() => {
    const currentIds = new Set(value.map(getValue));
    const newItems = filteredOptions.filter((opt) => !currentIds.has(getValue(opt)));
    onChange([...value, ...newItems]);
  }, [value, filteredOptions, getValue, onChange]);

  const handleRemoveAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const handleRemove = useCallback(
    (option: T) => {
      const newValue = value.filter((item) => getValue(item) !== getValue(option));
      onChange(newValue);
      if (newValue.length <= limit) setExpanded(false);
    },
    [value, onChange, getValue, limit]
  );

  const selectedCount = value.length;
  const visibleItems = expanded ? value : value.slice(0, limit);
  const hiddenCount = value.length - limit;

  return (
    <div className="w-full">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <div
            className={cn(
              'w-full min-h-[40px] px-3 py-2 flex items-center justify-between gap-2 cursor-pointer',
              'border border-[#D9D9D9] rounded-lg bg-white text-left',
              'hover:border-[#0C8DBF] focus:border-[#0C8DBF] focus:outline-none',
              disabled && 'opacity-50 cursor-not-allowed',
              isOpen && 'border-[#0C8DBF]'
            )}
          >
            {/* Display selected items as chips or text */}
            {isDisplaySelectedItem && value.length > 0 ? (
              <div className="flex-1 flex flex-wrap items-center gap-1">
                {visibleItems.map((item) => {
                  const onRemoveItem = () => handleRemove(item);

                  // Custom render selected item
                  if (renderSelectedItem) {
                    return (
                      <div key={getValue(item)} onClick={(e) => e.stopPropagation()}>
                        {renderSelectedItem(item, onRemoveItem)}
                      </div>
                    );
                  }

                  // Default chip render
                  return (
                    <div
                      key={getValue(item)}
                      className="flex items-center gap-[6px] border border-[#0C8DBF] bg-[#E4F8FF] px-[3px] py-[1px] rounded-[4px]"
                    >
                      <span className="text-[11px] text-[#0C8DBF]">{getLabel(item)}</span>
                      <div
                        className="hover:opacity-70 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveItem();
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
                          <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#0C8DBF" strokeLinecap="round" />
                        </svg>
                      </div>
                    </div>
                  );
                })}

                {/* Show more / Show less toggle */}
                {hiddenCount > 0 && !expanded && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(true);
                    }}
                    className="text-[11px] text-[#0C8DBF] hover:underline cursor-pointer"
                  >
                    +{hiddenCount} more
                  </div>
                )}
                {expanded && value.length > limit && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(false);
                    }}
                    className="text-[11px] text-[#0C8DBF] hover:underline cursor-pointer"
                  >
                    Show less
                  </div>
                )}
              </div>
            ) : (
              <span className={cn('text-[14px] flex-1 truncate', value.length === 0 && 'text-[#9D9D9C]')}>
                {value.length === 0 ? placeholder : value.map(getLabel).join(', ')}
              </span>
            )}

            <div className="flex items-center gap-1 shrink-0">
              {value.length > 0 && !disabled && (
                <div
                  onClick={handleClearAll}
                  className="p-0.5 mr-1 hover:bg-red-500/10 rounded transition-colors cursor-pointer"
                >
                  <svg
                    className="w-[18px] h-[18px] text-red-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </div>
              )}
              <CaretDownIcon
                className={cn(
                  'w-[12px] h-[12px] transition-transform',
                  isOpen ? 'rotate-180 text-[#0C8DBF]' : 'text-[#9D9D9C]'
                )}
              />
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 overflow-hidden rounded-lg border border-[#D9D9D9]"
          align="start"
          sideOffset={4}
        >
          <div className="flex flex-col">
            {/* Search */}
            {isWithSearch ? (
              <div className="p-2 border-b border-[#D9D9D9]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9D9D9C]" />
                  <input
                    disabled={isLoading || disabled}
                    type="text"
                    className="w-full h-9 pl-8 pr-3 text-[12px] border border-[#D9D9D9] rounded-lg focus:border-[#0C8DBF] outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
            ) : null}

            {/* Options List */}
            <div ref={parentRef} className="max-h-[200px] overflow-y-auto relative">
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#0C8DBF]" />
                </div>
              ) : null}

              {!isLoading && filteredOptions.length === 0 ? (
                <div className="flex justify-center py-4 text-[12px] text-[#9D9D9C]">{noOptionsFoundText}</div>
              ) : null}

              {!isLoading && filteredOptions.length > 0 ? (
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize() || filteredOptions.length * 36}px`,
                    position: 'relative',
                    width: '100%',
                  }}
                >
                  {(rowVirtualizer.getVirtualItems().length > 0
                    ? rowVirtualizer.getVirtualItems()
                    : filteredOptions.map((_, index) => ({ index, start: index * 36, size: 36 }))
                  ).map((virtualRow) => {
                    const option = filteredOptions[virtualRow.index];
                    const checked = isSelected(option);

                    const handleItemToggle = (c: boolean) => handleToggle(option, c);

                    // Custom render option - full control over design
                    if (renderOption) {
                      return (
                        <div
                          key={getValue(option)}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          {renderOption({
                            option,
                            isSelected: checked,
                            label: getLabel(option),
                            onToggle: handleItemToggle,
                          })}
                        </div>
                      );
                    }

                    // Default render
                    return (
                      <div
                        key={getValue(option)}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#0C8DBF] group',
                          checked && 'bg-[#0C8DBF]'
                        )}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                        onClick={() => handleItemToggle(!checked)}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(c) => handleItemToggle(c as boolean)}
                          className="border-[#25151E] group-hover:border-[#fff] data-[state=checked]:border-[#fff] data-[state=checked]:bg-[#fff] data-[state=checked]:text-[#0c8dbf]"
                        />
                        <span
                          className={cn('text-[12px] text-[#25151E] group-hover:text-[#fff]', checked && 'text-[#fff]')}
                        >
                          {getLabel(option)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Footer with Select All / Clear All */}
            <div className="flex items-center justify-between gap-2 p-2 border-t border-[#D9D9D9] bg-[#FAFAFA]">
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[#9D9D9C]">{selectedCount} selected</span>
                {selectedCount < filteredOptions.length && !isLoading && !disabled ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleSelectAll();
                    }}
                    className="text-[12px] text-[#0C8DBF] cursor-pointer hover:underline"
                  >
                    Select All
                  </div>
                ) : null}
                {selectedCount > 0 && !isLoading && !disabled ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveAll();
                    }}
                    className="text-[12px] text-[#E53935] cursor-pointer hover:underline"
                  >
                    Clear All
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MultiSelectDropdown;

export const CaretDownIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 5" fill="none" {...props}>
      <path
        d="M8.14061 0.640313L4.39061 4.39031C4.35578 4.42518 4.31442 4.45284 4.2689 4.47171C4.22337 4.49058 4.17458 4.50029 4.1253 4.50029C4.07601 4.50029 4.02722 4.49058 3.98169 4.47171C3.93617 4.45284 3.89481 4.42518 3.85998 4.39031L0.109983 0.640313C0.0574786 0.587867 0.0217158 0.521022 0.00722185 0.44824C-0.00727205 0.375458 0.000154685 0.300012 0.0285619 0.231454C0.056969 0.162895 0.105079 0.104307 0.166802 0.063105C0.228525 0.0219034 0.301084 -5.82853e-05 0.375295 1.16175e-07H7.8753C7.94951 -5.82853e-05 8.02207 0.0219034 8.08379 0.063105C8.14551 0.104307 8.19362 0.162895 8.22203 0.231454C8.25044 0.300012 8.25786 0.375458 8.24337 0.44824C8.22888 0.521022 8.19311 0.587867 8.14061 0.640313Z"
        fill="currentColor"
      />
    </svg>
  );
};
